import { CircularProgress } from '@mui/material';
import React, { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { tenant } from '../../../config/tenant';
import { postEsqueceuSenha } from '../../../services/user';
import { useLogin } from '../../authentication/hooks/useLogin';
import { ArrowIcon, EyeIcon, WhatsAppDot } from './icons';
import { WHATSAPP_MESSAGES, whatsappLink } from '../whatsapp';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type CardMode = 'login' | 'forgot';

/**
 * Card "Acesse sua conta" da home pública.
 * Integrado ao fluxo de autenticação da SPA via useLogin
 * (loginService -> saveSession -> getUserById -> stores).
 * "Esqueci minha senha" troca o formulário e chama POST /Usuarios/EsqueceuSenha.
 */
const HomeLoginCard: React.FC = () => {
	const { email, setEmail, password, setPassword, rememberMe, setRememberMe, error, loading, submit } =
		useLogin();
	const [showPassword, setShowPassword] = useState(false);
	const [mode, setMode] = useState<CardMode>('login');
	const [forgotLoading, setForgotLoading] = useState(false);
	const [forgotMessage, setForgotMessage] = useState('');
	const [forgotOk, setForgotOk] = useState(false);

	const openForgot = () => {
		setForgotMessage('');
		setForgotOk(false);
		setMode('forgot');
	};

	const backToLogin = () => {
		setForgotMessage('');
		setForgotOk(false);
		setMode('login');
	};

	const submitForgot = async (event: FormEvent) => {
		event.preventDefault();
		const value = email.trim();

		if (!value || !EMAIL_RE.test(value)) {
			setForgotOk(false);
			setForgotMessage('Informe um e-mail válido.');
			return;
		}

		setForgotLoading(true);
		setForgotMessage('');

		try {
			const message = await postEsqueceuSenha(value);
			const ok = message.toLowerCase().includes('sucesso');
			setForgotOk(ok);
			setForgotMessage(
				message || (ok ? 'Senha atualizada com sucesso!' : 'Não foi possível atualizar a senha.')
			);
		} catch {
			setForgotOk(false);
			setForgotMessage('Não foi possível enviar a solicitação. Tente novamente em instantes.');
		} finally {
			setForgotLoading(false);
		}
	};

	return (
		<div className='cr-login' id='login' role='region' aria-labelledby='cr-login-title'>
			<span className='cr-login__title' id='cr-login-title'>
				{mode === 'login' ? 'Acesse sua conta' : 'Esqueci minha senha'}
			</span>
			<span className='cr-login__sub'>
				{mode === 'login'
					? 'Entre com suas credenciais para acessar a plataforma.'
					: 'Informe o e-mail da sua conta. Enviaremos uma nova senha.'}
			</span>

			{mode === 'login' ? (
				<form className='cr-login__form' onSubmit={submit} noValidate>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='cr-login-email'>
							E-mail
						</label>
						<input
							id='cr-login-email'
							className='cr-field'
							type='email'
							placeholder='voce@suaempresa.com.br'
							autoComplete='username'
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							disabled={loading}
							required
						/>
					</div>

					<div className='cr-group'>
						<label className='cr-label' htmlFor='cr-login-password'>
							Senha
						</label>
						<div className='cr-pass'>
							<input
								id='cr-login-password'
								className='cr-field'
								type={showPassword ? 'text' : 'password'}
								placeholder='Sua senha'
								autoComplete='current-password'
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								disabled={loading}
								required
							/>
							<button
								type='button'
								className='cr-icon-btn cr-pass__toggle'
								onClick={() => setShowPassword((v) => !v)}
								aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
								aria-pressed={showPassword}
							>
								<EyeIcon off={showPassword} />
							</button>
						</div>
					</div>

					<label className='cr-check'>
						<input
							type='checkbox'
							checked={rememberMe}
							onChange={(e) => setRememberMe(e.target.checked)}
							disabled={loading}
						/>
						Manter-me conectado
					</label>

					<button type='submit' className='cr-btn cr-btn--block' disabled={loading}>
						{loading ? <CircularProgress size={18} color='inherit' /> : 'Acessar Plataforma'}
					</button>

					{error && (
						<span className='cr-error cr-error--box' role='alert'>
							{error}
						</span>
					)}
				</form>
			) : (
				<form className='cr-login__form' onSubmit={submitForgot} noValidate>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='cr-forgot-email'>
							E-mail
						</label>
						<input
							id='cr-forgot-email'
							className='cr-field'
							type='email'
							placeholder='voce@suaempresa.com.br'
							autoComplete='email'
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
								setForgotMessage('');
							}}
							disabled={forgotLoading}
							required
						/>
					</div>

					<button type='submit' className='cr-btn cr-btn--block' disabled={forgotLoading}>
						{forgotLoading ? <CircularProgress size={18} color='inherit' /> : 'Enviar nova senha'}
					</button>

					{forgotMessage && (
						<span
							className={forgotOk ? 'cr-success' : 'cr-error cr-error--box'}
							role={forgotOk ? 'status' : 'alert'}
						>
							{forgotMessage}
						</span>
					)}
				</form>
			)}

			{mode === 'login' ? (
				<button type='button' className='cr-login__small' onClick={openForgot}>
					Esqueci minha senha
				</button>
			) : (
				<button type='button' className='cr-login__small' onClick={backToLogin} disabled={forgotLoading}>
					Voltar ao login
				</button>
			)}

			<p className='cr-login__signup'>
				Ainda não tem cadastro? <Link to='/cadastro'>Cadastre-se</Link>
			</p>

			<a
				className='cr-wa-pill'
				href={whatsappLink(WHATSAPP_MESSAGES.info)}
				target='_blank'
				rel='noopener noreferrer'
			>
				<WhatsAppDot />
				<span>Fale conosco no WhatsApp</span>
				<ArrowIcon stroke={tenant.colors.accent} />
			</a>
		</div>
	);
};

export default HomeLoginCard;
